// import { CustomObjectContainerCard } from "components/ObjectTemplateCanvas/CustomObjectContainerCard";
import { Button } from "components/Button/Button";
import { ActionDropdownButton } from "components/DropdownButton/ActionDropdownButton";
import { IconCollection } from "components/Icon/IconCollection";
import { CustomObjectContainerCard } from "components/ObjectTemplateCanvas/CustomObjectContainerCard";
import { CanvasNodeProps } from "components/ObjectTemplateCanvas/ObjectTemplateNode";
import { getChildrenSpacerHeight } from "components/ObjectTemplateCanvas/utils";
import { Direction } from "containers/Resources/screens/ResourcesSingle/components/Visualization/visualization.service";
import { Object_Template } from "graphql/generated/graphql";
import { memo } from "react";
import { useHistory } from "react-router";
import "reactflow/dist/style.css";
import styles from "./CustomObjectNode.module.scss";

interface CustomObjectChildNodeProps {
    value: Object_Template;
    layoutDirection: Direction;
    children: any;
    childrenHeightData: { [id: string]: number };
    readOnly?: boolean;
    /** Load the next page (or all) custom objects for this template container. */
    loadMoreForTemplate?: (templateId: string, loadAll?: boolean) => void;
}

export const CustomObjectContainerNode = memo(
    ({ id, data }: CanvasNodeProps<CustomObjectChildNodeProps>) => {
        const history = useHistory();
        const { childrenHeightData } = data;
        const measuredChildrenHeight = childrenHeightData?.[id] ?? 0;

        const readOnly = data.readOnly ?? false;

        // The container reserves vertical space for its absolutely-positioned child
        // object nodes (a DOM spacer) so the footer count line lands just below the
        // last card in normal flow. The child cards are laid out deterministically by
        // dagre (real card height + gap, at a fixed top offset below the header), so
        // the required spacer height is derived exactly from the loaded child count via
        // getChildrenSpacerHeight — no dependency on a DOM measurement for the common
        // all-collapsed canvas. A deferred DOM measurement (`measuredChildrenHeight`)
        // can only ever ENLARGE the reservation, to cover the taller footprint of an
        // expanded card; it never shrinks it below the deterministic collapsed floor
        // (which would pull the footer up over the cards). A genuinely empty container
        // reserves nothing and shows the "No custom objects." placeholder.
        const childNodes = (data.children ?? []) as Array<unknown>;
        const hasChildren = childNodes.length > 0;
        const deterministicChildrenHeight = getChildrenSpacerHeight(childNodes.length);
        const childrenHeight = hasChildren
            ? Math.max(deterministicChildrenHeight, measuredChildrenHeight)
            : 0;

        // Progressive loading: the container may hold only the first page of its
        // custom objects. Surface how many are shown vs. the total and let the
        // user reveal the rest (interactive canvas only; not the read-only embed).
        const totalCount =
            data?.value?.custom_object_list_object_template_id?.custom_object_count ?? 0;
        const loadedCount =
            data?.value?.custom_object_list_object_template_id?.custom_object_list?.length ?? 0;
        const hasMore = !readOnly && !!data.loadMoreForTemplate && loadedCount < totalCount;

        return (
            <div className={`nowheel ${styles.customObjectParentNode}`}>
                <CustomObjectContainerCard
                    data={data.value}
                    history={history}
                    showCountBadge={readOnly}
                />
                {childrenHeight > 0 ? <div style={{ height: childrenHeight }}></div> : null}
                {/* Footer: always show the object count in the interactive canvas
                    (item 12 — count moved out of the header so the name gets full
                    width). The Create button was removed (item 3, Margot's explicit
                    proposal). When more objects exist than are loaded, the count
                    reads "loaded / total" and the Load more split button (Load all
                    in its menu) appears beneath it. Keep
                    the reserved height in getContainerHeight (utils.ts) in sync. */}
                {!readOnly && (
                    <div className={styles.containerFooter}>
                        <span className={styles.footerCount}>
                            {hasMore
                                ? `${loadedCount} / ${totalCount} objects`
                                : `${totalCount} ${totalCount === 1 ? "object" : "objects"}`}
                        </span>
                        {hasMore && (
                            <div className={styles.loadMoreButtons}>
                                {/* Split button (Margot 2026-09-22): "Load more"
                                    loads the next page; "Load all" is the secondary
                                    action in its menu. */}
                                <ActionDropdownButton
                                    parentAction={() => data.loadMoreForTemplate?.(id)}
                                    label="Load more"
                                    icon={IconCollection.chevronDown}
                                    size="small"
                                >
                                    <Button
                                        label="Load all"
                                        icon={IconCollection.chevronDoubleDown}
                                        variation="clear"
                                        className="wide"
                                        onClick={() => data.loadMoreForTemplate?.(id, true)}
                                    />
                                </ActionDropdownButton>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
);
